import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  ProgramCreationProvider,
  useProgramCreation
} from '../../../../../../components/admin3/newProgram/ProgramCreationContext';
import ProgramUsers from '../program tabs/ProgramUsers';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { fetchGroupDetails } from '../../../../../../redux/slices/group-details/groupDetails';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import InviteFinishDialog, {
  INVITE_FINISH_GATE_COPY,
  useInviteAndFinish
} from '../../../aimd-v2/components/InviteFinishDialog';

interface Props {
  groupId: string;
  // Called once Invite & Finish has enrolled the audience (program is Invited).
  onFinish: () => void;
}

const CreationMembersStepInner: React.FC<Props> = ({ groupId, onFinish }) => {
  const dispatch = useAppDispatch();
  const { dispatch: pcDispatch } = useProgramCreation();
  const [ready, setReady] = useState(false);
  const [canFinish, setCanFinish] = useState(false);
  const inviteFinish = useInviteAndFinish(groupId, onFinish);

  // ProgramUsers reads the group from the ProgramCreation context and Redux.
  useEffect(() => {
    pcDispatch({ type: 'SET_GROUP_ID', payload: groupId });
    dispatch(fetchGroupDetails({ grpId: groupId }) as any);
    setReady(true);
  }, [groupId, pcDispatch, dispatch]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#0F0F0F' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 30, fontWeight: 500, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
            People
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 400 }}>
          {ready && <ProgramUsers creationFlow onInviteReadinessChange={setCanFinish} />}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid #2A2A2A',
          flexShrink: 0
        }}
      >
        <Tooltip title={canFinish ? '' : INVITE_FINISH_GATE_COPY}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <DosenButton
              disabled={!canFinish || inviteFinish.finishing}
              loading={inviteFinish.finishing}
              loadingText="Enrolling..."
              onClick={inviteFinish.openConfirm}
            >
              Enroll People
            </DosenButton>
          </span>
        </Tooltip>
      </Box>
      <InviteFinishDialog {...inviteFinish.dialogProps} />
    </Box>
  );
};

/**
 * Members step for Manual and Mentorship builds in the Add Program modal (and
 * for Continue on any program waiting at the Invite stage) — the same audience
 * editor, intended-members list and Invite & Finish as the AI wizard's step.
 */
const CreationMembersStep: React.FC<Props> = (props) => (
  <ProgramCreationProvider>
    <CreationMembersStepInner {...props} />
  </ProgramCreationProvider>
);

export default CreationMembersStep;
